/**
 * Filtrar Transações por período, pela data de PAGAMENTO ou pela de COMPRA.
 *
 * Pedido dela, em 13/09, logo depois de as duas datas existirem: "quero que
 * em transações tenha a opção de filtrar por data de pagamento e data de
 * compra".
 *
 * ── As duas bases respondem perguntas diferentes, de propósito ───────────
 *
 * - **compra** — "o que eu comprei em outubro?". Uma compra é UM evento: a
 *   parcelada em 6x aparece uma vez, no dia em que foi comprada. Precisão de
 *   dia, sobre `dataDaCompraDe` (compra nula = a compra é o pagamento).
 *
 * - **pagamento** — "o que eu paguei em outubro?". Uma parcelada em 6x tem
 *   SEIS pagamentos, e uma assinatura tem um por mês. Filtrar só pela data
 *   gravada deixaria de fora toda parcela que cai no período — uma lista
 *   plausível, com um total menor que o real. Por isso esta base desdobra em
 *   ocorrências.
 *
 * É a mesma distinção que o backend já tinha em `transactionQuery`
 * (`purchase` e `affects`), só que com precisão de dia nas duas pontas.
 *
 * ── Sem terceira cópia da regra de parcela ───────────────────────────────
 *
 * Qual parcela cai em qual mês é decidido por `transactionsInMonth`, e só lá
 * (armadilha 4: foi ter três cópias que criou dois bugs de saldo). Este
 * arquivo pergunta mês a mês e só decide o DIA dentro do mês.
 *
 * ── Busca em TODAS as transações ─────────────────────────────────────────
 *
 * Quem chama passa a lista inteira, não a do mês da tela. Uma compra de
 * outubro paga em outubro não está na lista de novembro, e o período
 * "01/10–31/10" olhado com novembro na tela a perderia em silêncio.
 *
 * Puro e testável sem renderizar nada.
 */
import { Transaction } from '../types/Transaction';
import { transactionsInMonth } from './monthView';
import { dataDaCompraDe, diasNoMes, hojeISOLocal } from './dataDoCartao';

export type BaseDaData = 'pagamento' | 'compra';

export interface Periodo {
  base: BaseDaData;
  /** 'AAAA-MM-DD', ou vazio para "desde sempre". */
  de: string;
  /** 'AAAA-MM-DD', ou vazio para "sem fim". */
  ate: string;
}

export const PERIODO_VAZIO: Periodo = { base: 'pagamento', de: '', ate: '' };

export const periodoAtivo = (p: Periodo): boolean => !!(p.de || p.ate);

/** "De" depois de "Até" não é um período — é um engano de digitação. */
export const periodoInvertido = (p: Periodo): boolean => !!(p.de && p.ate && p.de > p.ate);

const dia = (s: string) => String(s).slice(0, 10);
const pad = (n: number) => String(n).padStart(2, '0');
const mesDe = (iso: string) => iso.slice(0, 7);

const somaMeses = (mes: string, n: number): string => {
  const [a, m] = mes.split('-').map(Number);
  const abs = a * 12 + (m - 1) + n;
  return `${Math.floor(abs / 12)}-${pad((abs % 12) + 1)}`;
};

/**
 * O último mês em que a transação ainda paga alguma coisa.
 *
 * Só serve para fechar um "Até" em branco: sem fim, a base pagamento teria de
 * perguntar mês a mês até o infinito por causa das assinaturas.
 */
const ultimoMes = (t: Transaction, hojeISO: string): string => {
  const inicio = mesDe(dia(t.date));
  if (t.isMonthly) return t.endDate ? mesDe(dia(t.endDate)) : mesDe(hojeISO);
  if (t.hasInstallments && t.installments) {
    const atual = Math.min(Math.max(t.currentInstallment ?? 1, 1), t.installments);
    return somaMeses(inicio, t.installments - atual);
  }
  return inicio;
};

/**
 * Teto de 10 anos de meses, igual ao backend: uma data absurda digitada não
 * pode virar um laço que trava a tela.
 */
const TETO_DE_MESES = 120;

export const filtrarPorPeriodo = (
  transacoes: Transaction[],
  p: Periodo,
  hojeISO: string = hojeISOLocal(),
): Transaction[] => {
  if (!periodoAtivo(p)) return transacoes;
  if (periodoInvertido(p)) return [];

  if (p.base === 'compra') {
    return transacoes.filter(t => {
      const d = dia(dataDaCompraDe(t));
      return (!p.de || d >= p.de) && (!p.ate || d <= p.ate);
    });
  }

  if (transacoes.length === 0) return [];

  const de = p.de || transacoes.map(t => dia(t.date)).sort()[0];
  const ate = p.ate || `${transacoes.map(t => ultimoMes(t, hojeISO)).sort().slice(-1)[0]}-31`;

  const saida: Transaction[] = [];
  let mes = mesDe(de);
  for (let n = 0; mes <= mesDe(ate) && n < TETO_DE_MESES; n++, mes = somaMeses(mes, 1)) {
    const [ano, m] = mes.split('-').map(Number);
    for (const o of transactionsInMonth(transacoes, mes)) {
      // O dia do pagamento se repete todo mês; dia 31 num mês de 30 vira o 30.
      const diaDoMes = Math.min(Number(dia(o.date).slice(8, 10)) || 1, diasNoMes(ano, m));
      const quando = `${mes}-${pad(diaDoMes)}`;
      if (quando < de || quando > ate) continue;

      // `displayOcorrencia` é SÓ de exibição, como `displayInstallment`: duas
      // mensalidades da mesma assinatura no período precisam de chaves
      // diferentes na lista, e nenhuma delas pode voltar ao banco.
      saida.push({ ...o, displayOcorrencia: mes });
    }
  }
  return saida;
};
